"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/lib/supabase/client";
import {
  RiAlertFill,
  RiBugFill,
  RiCheckFill,
  RiFileCopyLine,
  RiHomeFill,
  RiRefreshLine,
} from "@remixicon/react";

export default function Error({
  error,
  reset,
}: {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [errorId, setErrorId] = useState<string | null>(null);

  const errorDetails = useMemo(() => {
    return {
      statusCode: 500,
      title: error.name || "Error",
      message: error.message || "Error",
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "N/A",
      url: typeof window !== "undefined" ? window.location.href : "N/A",
      stack: error.stack,
      id: "",
    };
  }, [error.message, error.name, error.stack]);

  const copyErrorDetails = async () => {
    const errorText = `
Reporte de Error
================
Código de Estado: ${errorDetails.statusCode}
Título: ${errorDetails.title}
Mensaje: ${errorDetails.message}
Marca de Tiempo: ${errorDetails.timestamp}
URL: ${errorDetails.url}
Agente de Usuario: ${errorDetails.userAgent}
ID del Error: ${errorDetails.id}

Seguimiento de Pila:
${errorDetails.stack}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      toast.success("¡Copiado!", {
        description: "Los detalles del error se han copiado al portapapeles.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {
      toast.error("Error al copiar", {
        description: "No se pudieron copiar los detalles del error.",
      });
    }
  };

  const reportError = () => {
    // Aquí podrías integrar con un servicio de reporte de errores como Sentry, Bugsnag, etc.
    const subject = encodeURIComponent(
      `Reporte de Error - ${errorDetails.statusCode}: ${errorDetails.title}`,
    );
    const body = encodeURIComponent(`
Hola,

Encontré un error en la aplicación:

Código de Estado: ${errorDetails.statusCode}
Título: ${errorDetails.title}
Mensaje: ${errorDetails.message}
Marca de Tiempo: ${errorDetails.timestamp}
URL: ${errorDetails.url}
ID del Error: ${errorDetails.id}

Detalles adicionales:
[Describe qué estabas haciendo cuando ocurrió el error]

Seguimiento de Pila:
${errorDetails.stack}
    `);

    window.open(`mailto:orderfast.adm@gmail.com?subject=${subject}&body=${body}`, "_blank");
  };

  const goHome = () => {
    window.location.href = "/";
  };

  useEffect(() => {
    if (errorId) return;
    if (process.env.NODE_ENV === "development") return;

    toast
      .promise(
        async () => {
          const { data, error } = await supabase
            .from("error_details")
            .insert({
              url: errorDetails.url,
              user_agent: errorDetails.userAgent,
              title: errorDetails.title,
              message: errorDetails.message,
              status_code: errorDetails.statusCode,
              stacktrace: errorDetails.stack,
            })
            .select()
            .single();

          if (error) {
            throw error;
          }

          return data;
        },
        {
          loading: "Guardando error...",
          success: "Error guardado correctamente",
          error: "Error al guardar el error",
        },
      )
      .unwrap()
      .then((data) => {
        setErrorId(data.id);
        errorDetails.id = data.id;
      });
  }, [
    errorDetails,
    errorDetails.message,
    errorDetails.stack,
    errorDetails.statusCode,
    errorDetails.title,
    errorDetails.url,
    errorDetails.userAgent,
    errorId,
  ]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-4 scheme-only-light">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header con icono y código de error */}
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="relative">
              <div className="bg-destructive/20 flex h-24 w-24 items-center justify-center rounded-full">
                <RiAlertFill className="text-destructive-foreground h-12 w-12" />
              </div>
              <Badge className="absolute -top-2 -right-2 text-xs font-bold" variant="destructive">
                {errorDetails.statusCode}
              </Badge>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold">{errorDetails.title}</h1>
            <p className="text-muted-foreground mx-auto max-w-md text-lg">{errorDetails.message}</p>
          </div>
        </div>

        {/* Card principal con detalles del error */}
        <Card className="shadow-lg backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <RiBugFill className="h-5 w-5" />
              Detalles del Error
            </CardTitle>
            <CardDescription>
              Información técnica que puede ayudar a resolver el problema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <div>
                <span className="text-muted-foreground font-medium">Código:</span>
                <span className="bg-muted ml-2 rounded px-2 py-1 font-mono">
                  {errorDetails.statusCode}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground font-medium">Marca de Tiempo:</span>
                <span className="text-muted-foreground ml-2 font-mono">
                  {new Date().toLocaleString()}
                </span>
              </div>
            </div>

            <Separator />

            {/* Stack trace colapsible */}
            <details className="group">
              <summary className="cursor-pointer font-medium transition-colors hover:text-gray-900">
                Seguimiento de Pila (Hacer clic para expandir)
              </summary>
              <div className="bg-muted mt-2 rounded-md border p-3">
                <pre className="text-muted-foreground overflow-x-auto text-xs whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </div>
            </details>
          </CardContent>
        </Card>

        {/* Botones de acción */}
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button className="flex items-center gap-2" onClick={goHome}>
            <RiHomeFill className="h-4 w-4" />
            Ir al Inicio
          </Button>

          <Button className="flex items-center gap-2" variant="outline" onClick={reset}>
            <RiRefreshLine className="h-4 w-4" />
            Reintentar
          </Button>

          <Button
            className="flex items-center gap-2"
            disabled={copied}
            variant="outline"
            onClick={copyErrorDetails}
          >
            {copied ? (
              <>
                <RiCheckFill className="h-4 w-4" />
                ¡Copiado!
              </>
            ) : (
              <>
                <RiFileCopyLine className="h-4 w-4" />
                Copiar Error
              </>
            )}
          </Button>

          <Button className="flex items-center gap-2" variant="destructive" onClick={reportError}>
            <RiBugFill className="h-4 w-4" />
            Reportar Error
          </Button>
        </div>

        {/* Footer informativo */}
        <div className="text-muted-foreground space-y-1 text-center text-sm">
          <p>Si el problema persiste, por favor contacta a nuestro equipo de soporte.</p>
          <p className="font-mono text-xs">ID del Error: {errorId}</p>
        </div>
      </div>
    </div>
  );
}
